import axios from "axios";
import {
  UserWithRoles,
  UserRole,
  RoleInfo,
  ApiResponse,
  Employee,
} from "../types/leave";

const API_BASE_URL = "/ofc/user-management";

// Configure axios defaults
axios.defaults.withCredentials = true;

class UserManagementApiService {
  // Current User Profile APIs
  async getMyProfile(): Promise<Employee> {
    const response = await axios.get<ApiResponse<Employee>>(
      `${API_BASE_URL}/me`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch profile");
    }
    return response.data.data;
  }

  // User Management APIs
  async getAllUsers(
    page: number = 0,
    size: number = 20,
  ): Promise<{ content: UserWithRoles[]; totalElements: number }> {
    const response = await axios.get<
      ApiResponse<{ content: UserWithRoles[]; totalElements: number }>
    >(`${API_BASE_URL}/employees?page=${page}&size=${size}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch users");
    }
    return response.data.data;
  }

  async searchUsers(
    searchTerm: string,
    page: number = 0,
    size: number = 20,
  ): Promise<{ content: UserWithRoles[]; totalElements: number }> {
    const response = await axios.get<
      ApiResponse<{ content: UserWithRoles[]; totalElements: number }>
    >(
      `${API_BASE_URL}/users/search?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&size=${size}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to search users");
    }
    return response.data.data;
  }

  async getUserRoles(userId: number): Promise<UserRole[]> {
    const response = await axios.get<ApiResponse<UserRole[]>>(
      `${API_BASE_URL}/users/${userId}/roles`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch user roles");
    }
    return response.data.data;
  }

  async assignRole(userId: number, role: UserRole): Promise<void> {
    const response = await axios.post<ApiResponse<string>>(
      `${API_BASE_URL}/users/${userId}/roles`,
      { role },
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to assign role");
    }
  }

  async removeRole(userId: number, role: UserRole): Promise<void> {
    const response = await axios.delete<ApiResponse<string>>(
      `${API_BASE_URL}/users/${userId}/roles/${role}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to remove role");
    }
  }

  async setUserRoles(userId: number, roles: UserRole[]): Promise<void> {
    const response = await axios.put<ApiResponse<string>>(
      `${API_BASE_URL}/employees/${userId}/roles`,
      { roles },
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to update user roles");
    }
  }

  async createUser(userData: {
    employeeId: string;
    username: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    ldapInitials?: string;
    email: string;
    birthDate?: string;
    homeAddress?: string;
    personalPhone?: string;
    secondaryPhone?: string;
    emergencyContactName?: string;
    emergencyContactRelationship?: string;
    emergencyContactPhone?: string;
    emergencyContactName2?: string;
    emergencyContactRelationship2?: string;
    emergencyContactPhone2?: string;
    jobTitle: string;
    department: string;
    officeLocation?: string;
    hireDate: string;
    startDate: string;
    roles: UserRole[];
    vacationAllocatedDays: number;
    vacationUsedDays: number;
    vacationCarriedOverDays: number;
  }): Promise<Employee> {
    // Helper function to convert empty strings to null
    const toNullIfEmpty = (value: any) => {
      if (value === "" || value === undefined) return null;
      return value;
    };

    const createDto = {
      employeeId: userData.employeeId,
      username: userData.username,
      firstName: userData.firstName,
      middleName: toNullIfEmpty(userData.middleName),
      lastName: userData.lastName,
      ldapInitials: toNullIfEmpty(userData.ldapInitials),
      email: userData.email,
      birthDate: toNullIfEmpty(userData.birthDate),
      homeAddress: toNullIfEmpty(userData.homeAddress),
      personalPhone: toNullIfEmpty(userData.personalPhone),
      secondaryPhone: toNullIfEmpty(userData.secondaryPhone),
      emergencyContactName: toNullIfEmpty(userData.emergencyContactName),
      emergencyContactRelationship: toNullIfEmpty(
        userData.emergencyContactRelationship,
      ),
      emergencyContactPhone: toNullIfEmpty(userData.emergencyContactPhone),
      emergencyContactName2: toNullIfEmpty(userData.emergencyContactName2),
      emergencyContactRelationship2: toNullIfEmpty(
        userData.emergencyContactRelationship2,
      ),
      emergencyContactPhone2: toNullIfEmpty(userData.emergencyContactPhone2),
      jobTitle: userData.jobTitle,
      department: userData.department,
      officeLocation: toNullIfEmpty(userData.officeLocation),
      hireDate: userData.hireDate,
      startDate: toNullIfEmpty(userData.startDate),
      roles: userData.roles,
      ctoAllocatedDays:
        userData.vacationAllocatedDays > 0
          ? userData.vacationAllocatedDays
          : null,
      ctoUsedDays:
        userData.vacationUsedDays > 0 ? userData.vacationUsedDays : null,
      ctoCarriedOverDays:
        userData.vacationCarriedOverDays > 0
          ? userData.vacationCarriedOverDays
          : null,
    };

    const response = await axios.post<ApiResponse<Employee>>(
      `${API_BASE_URL}/employees`,
      createDto,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to create user");
    }
    return response.data.data;
  }

  async updateUserProfile(userId: number, profile: any): Promise<Employee> {
    const updateDto = {
      id: profile.id,
      employeeId: profile.employeeId,
      username: profile.username,
      firstName: profile.firstName,
      middleName: profile.middleName,
      lastName: profile.lastName,
      ldapInitials: profile.ldapInitials,
      email: profile.email,
      birthDate: profile.birthDate,
      homeAddress: profile.homeAddress,
      personalPhone: profile.personalPhone,
      secondaryPhone: profile.secondaryPhone,
      emergencyContactName: profile.emergencyContactName,
      emergencyContactRelationship: profile.emergencyContactRelationship,
      emergencyContactPhone: profile.emergencyContactPhone,
      emergencyContactName2: profile.emergencyContactName2,
      emergencyContactRelationship2: profile.emergencyContactRelationship2,
      emergencyContactPhone2: profile.emergencyContactPhone2,
      jobTitle: profile.jobTitle,
      department: profile.department,
      officeLocation: profile.officeLocation,
      hireDate: profile.hireDate,
      startDate: profile.startDate,
      status: profile.status,
      roles: profile.roles,
      version: profile.version,
    };

    const response = await axios.put<ApiResponse<Employee>>(
      `${API_BASE_URL}/employees/${userId}`,
      updateDto,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update profile");
    }
    return response.data.data;
  }

  async activateUser(userId: number): Promise<void> {
    const response = await axios.put<ApiResponse<string>>(
      `${API_BASE_URL}/employees/${userId}/activate`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to activate user");
    }
  }

  async deactivateUser(userId: number): Promise<void> {
    const response = await axios.put<ApiResponse<string>>(
      `${API_BASE_URL}/employees/${userId}/deactivate`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to deactivate user");
    }
  }

  async unlockUser(userId: number): Promise<void> {
    const response = await axios.put<ApiResponse<string>>(
      `${API_BASE_URL}/employees/${userId}/unlock`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to unlock user");
    }
  }

  async deleteUser(userId: number): Promise<void> {
    const response = await axios.delete<ApiResponse<void>>(
      `${API_BASE_URL}/employees/${userId}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to delete user");
    }
  }

  // LDAP Sync APIs
  async syncUsersFromLdap(): Promise<any> {
    const response = await axios.post<ApiResponse<any>>(
      `${API_BASE_URL}/sync/ldap`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to sync users from LDAP");
    }
    return response.data.data;
  }

  async syncUserFromLdap(username: string): Promise<Employee> {
    const response = await axios.post<ApiResponse<Employee>>(
      `${API_BASE_URL}/sync/ldap/${username}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to sync user from LDAP");
    }
    return response.data.data;
  }

  async getSyncStatus(): Promise<any> {
    const response = await axios.get<ApiResponse<any>>(
      `${API_BASE_URL}/sync/status`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch sync status");
    }
    return response.data.data;
  }

  // Role Management APIs
  async getAllRoles(): Promise<RoleInfo[]> {
    const response = await axios.get<ApiResponse<RoleInfo[]>>(
      `${API_BASE_URL}/roles`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch roles");
    }
    return response.data.data;
  }
}

export default new UserManagementApiService();
